import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { apiClient } from '../apiClient';

export interface User {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  /** Present when backend supports ISSUE-242 email verification */
  email_verified?: boolean;
  is_active: boolean;
  is_superuser?: boolean;
  role?: string;
  /** Present on enhanced-auth /auth/context user payload */
  permissions?: string[];
  profile_data?: Record<string, any>;
  company_id?: string;
  /** Present when registration created company_instances (ISSUE-241) */
  company_instance_id?: string;
  last_login?: string;
  is_deleted?: boolean;
  deleted_at?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UserCreate {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  role?: string;
}

export interface UserUpdate {
  username?: string;
  email?: string;
  password?: string;
  full_name?: string;
  is_active?: boolean;
  role?: string;
  profile_data?: Record<string, any>;
  company_id?: string;
}

export interface UserFilters {
  role?: string;
  is_active?: boolean;
  company_id?: string;
  limit?: number;
  offset?: number;
}

export interface UserCompanyAssignment {
  company_id: string;
  user_id: string;
  role?: string;
  is_active?: boolean;
}

export interface UserPermission {
  id: string;
  permission_name: string;
  permission_type: string;
  granted_by?: string;
  granted_at?: string;
}

export interface UserActivity {
  id: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface UserPreferences {
  [key: string]: any;
}

export class UserService extends BaseService {
  /**
   * Transform backend user response (first_name, last_name) to frontend format (full_name)
   */
  private static transformUserResponse(user: any): User {
    let profile_data = user.profile_data;
    if (typeof profile_data === 'string') {
      try {
        profile_data = JSON.parse(profile_data);
        if (typeof profile_data === 'string') {
          profile_data = JSON.parse(profile_data);
        }
      } catch {
        profile_data = profile_data ? { value: profile_data } : undefined;
      }
    }
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const role =
      (typeof user.role === 'string' && user.role) ||
      roles.find((entry: unknown) => typeof entry === 'string' && entry) ||
      undefined;
    const composedName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return {
      ...user,
      role,
      profile_data:
        profile_data && typeof profile_data === 'object' ? profile_data : undefined,
      full_name: composedName || user.full_name || undefined,
    };
  }

  /**
   * Transform array of backend user responses to frontend format
   */
  private static transformUserArrayResponse(users: any[]): User[] {
    return users.map(user => this.transformUserResponse(user));
  }

  /**
   * Get all users with optional filtering
   */
  static async getAll(filters?: UserFilters): Promise<User[]> {
    try {
      let endpoint = API_ENDPOINTS.USERS.BASE;
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.role) params.append('role', filters.role);
        if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
        if (filters.company_id) params.append('company_id', filters.company_id);
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.offset) params.append('offset', String(filters.offset));
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      // Try dev users endpoint first (no auth required)
      try {
        const users = await this.get<any[]>(API_ENDPOINTS.DEV_USERS.BASE + (params.toString() ? `?${params.toString()}` : ''));
        return this.transformUserArrayResponse(users);
      } catch (error) {
        console.error('Error fetching users from dev endpoint:', error);
        // Fallback to auth endpoint if dev endpoint fails
        return this.get<User[]>(endpoint);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  /**
   * Platform directory. Uses GET /users only. Errors propagate.
   * Does not call /dev/users.
   */
  static async listForPlatformDirectory(filters?: UserFilters): Promise<User[]> {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    params.append('limit', String(filters?.limit ?? 1000));
    params.append('offset', String(filters?.offset ?? 0));
    const users = await this.get<unknown[]>(`${API_ENDPOINTS.USERS.BASE}?${params.toString()}`);
    if (!Array.isArray(users)) {
      throw new Error('GET /users did not return a list.');
    }
    return this.transformUserArrayResponse(users);
  }

  /** POST /users. Does not call /dev/users. */
  static async createForPlatformDirectory(userData: UserCreate): Promise<User> {
    const password = String(userData.password || '');
    if (password.trim().length < 8) {
      throw new Error('Password must be at least 8 characters.');
    }
    const created = await this.post<unknown>(API_ENDPOINTS.USERS.BASE, {
      username: userData.username,
      email: userData.email,
      password,
      full_name: userData.full_name,
      role: userData.role,
    });
    return this.transformUserResponse(created);
  }

  /** PUT /users/{id}. Does not call /dev/users. Password changes use POST /auth/forgot-password. */
  static async updateForPlatformDirectory(id: string, userData: UserUpdate): Promise<User> {
    const payload: Record<string, unknown> = {};
    if (userData.username !== undefined) payload.username = userData.username;
    if (userData.email !== undefined) payload.email = userData.email;
    if (userData.full_name !== undefined) payload.full_name = userData.full_name;
    if (userData.is_active !== undefined) payload.is_active = userData.is_active;
    if (userData.role !== undefined) payload.role = userData.role;
    if (userData.profile_data !== undefined) payload.profile_data = userData.profile_data;
    if (Object.keys(payload).length === 0) {
      throw new Error('No user fields to save.');
    }
    const updated = await this.put<unknown>(API_ENDPOINTS.USERS.BY_ID(id), payload);
    return this.transformUserResponse(updated);
  }

  /**
   * Get user by ID
   */
  static async getById(id: string): Promise<User> {
    // Validate UUID format before making request
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      throw new Error(`Invalid user ID format. Expected UUID, got: ${id}`);
    }
    
    try {
      // Try dev users endpoint first (no auth required)
      const user = await this.get<any>(API_ENDPOINTS.DEV_USERS.BY_ID(id));
      return this.transformUserResponse(user);
    } catch (error) {
      console.error('Error fetching user from dev endpoint:', error);
      // Fallback to auth endpoint if dev endpoint fails
      return this.get<User>(API_ENDPOINTS.USERS.BY_ID(id));
    }
  }

  /**
   * Create a new user
   */
  static async create(userData: UserCreate): Promise<User> {
    // Transform frontend format to backend format
    const backendData: Record<string, unknown> = {
      username: userData.username,
      email: userData.email,
      password: userData.password,
    };
    
    // Split full_name into first_name and last_name
    if (userData.full_name) {
      const fullName = userData.full_name.trim();
      if (fullName) {
        const parts = fullName.split(/\s+/);
        backendData.first_name = parts[0] || null;
        backendData.last_name = parts.slice(1).join(' ') || null;
      }
    }
    
    if (userData.role) {
      // Note: role is not supported by backend endpoint, but we'll include it
      // in case the backend accepts it or handles it separately
      backendData.role = userData.role;
    }
    
    try {
      // Try dev users endpoint first (no auth required)
      const user = await this.post<any>(API_ENDPOINTS.DEV_USERS.BASE, backendData);
      return this.transformUserResponse(user);
    } catch (error) {
      console.error('Error creating user from dev endpoint:', error);
      // Fallback to auth endpoint if dev endpoint fails
      return this.post<User>(API_ENDPOINTS.USERS.BASE, userData as unknown as Record<string, unknown>);
    }
  }

  /**
   * Update user
   * Uses /dev/users/{id} endpoint which accepts UUID strings
   * Note: The /users/{id} endpoint expects integer IDs, so it cannot be used as a fallback
   * 
   * Transforms frontend UserUpdate format to backend format:
   * - Splits full_name into first_name and last_name
   * - Removes role, profile_data, company_id (not supported by backend endpoint)
   */
  static async update(id: string, userData: UserUpdate): Promise<User> {
    const devEndpoint = API_ENDPOINTS.DEV_USERS.BY_ID(id);
    
    // Transform frontend format to backend format
    const backendData: Record<string, unknown> = {};
    
    // Map supported fields
    if (userData.username !== undefined) backendData.username = userData.username;
    if (userData.email !== undefined) backendData.email = userData.email;
    if (userData.password !== undefined) backendData.password = userData.password;
    if (userData.is_active !== undefined) backendData.is_active = userData.is_active;
    
    // Split full_name into first_name and last_name
    if (userData.full_name !== undefined) {
      const fullName = userData.full_name.trim();
      if (fullName) {
        const parts = fullName.split(/\s+/);
        backendData.first_name = parts[0] || null;
        backendData.last_name = parts.slice(1).join(' ') || null;
      } else {
        backendData.first_name = null;
        backendData.last_name = null;
      }
    }
    
    // Note: role, profile_data, and company_id are not supported by the backend endpoint
    // These would need to be handled through separate endpoints if needed
    
    console.log('[UserService] Updating user:', {
      userId: id,
      endpoint: devEndpoint,
      frontendData: userData,
      backendData: backendData
    });
    
    try {
      const result = await this.put<any>(devEndpoint, backendData);
      console.log('[UserService] User updated successfully');
      
      // Transform backend response (first_name, last_name) to frontend format (full_name)
      const transformedResult: User = {
        ...result,
        full_name: result.first_name || result.last_name 
          ? [result.first_name, result.last_name].filter(Boolean).join(' ') 
          : undefined,
      };
      
      return transformedResult;
    } catch (error: any) {
      // Check if it's a network/CORS error
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('Network Error') ||
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin') ||
                         (!error.response && error.request);
      
      // Suppress CORS error logging (backend configuration issue)
      if (!isCorsError) {
        console.error('[UserService] Error updating user:', {
          status: error.response?.status,
          data: error.response?.data,
          endpoint: devEndpoint,
          message: error.message
        });
      } else {
        // Log a user-friendly message for CORS errors
        console.warn('[UserService] Network/CORS error updating user (this is typically a backend configuration issue):', {
          endpoint: devEndpoint,
          error: error.message || error.code
        });
      }
      
      // Re-throw with a more user-friendly message if it's a CORS error
      if (isCorsError) {
        const corsError = new Error('Unable to connect to the server. This may be due to CORS configuration or network issues.');
        (corsError as any).code = error.code;
        (corsError as any).response = error.response;
        throw corsError;
      }
      
      throw error;
    }
  }

  /**
   * Update user's role
   */
  static async updateRole(id: string, role: string): Promise<User> {
    return this.put<User>(
      API_ENDPOINTS.USERS.ROLE(id),
      { role } as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete user
   */
  static async delete(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.USERS.BY_ID(id));
  }

  /**
   * Get current user (me)
   */
  static async getMe(): Promise<User> {
    return this.get<User>(API_ENDPOINTS.USERS.ME);
  }

  /**
   * Update current user
   */
  static async updateMe(userData: UserUpdate): Promise<User> {
    return this.put<User>(API_ENDPOINTS.USERS.ME, userData as unknown as Record<string, unknown>);
  }

  /**
   * Get all companies for a user
   */
  static async getCompanies(userId: string): Promise<any[]> {
    try {
      return this.get<any[]>(API_ENDPOINTS.USERS.COMPANIES(userId));
    } catch (error) {
      console.error('[UserService] Error fetching user companies:', error);
      return [];
    }
  }

  /**
   * Add user to company
   */
  static async addToCompany(userId: string, companyId: string, role?: string, isActive?: boolean): Promise<any> {
    return this.post<any>(
      API_ENDPOINTS.USERS.ADD_TO_COMPANY(userId, companyId),
      {
        role: role || 'member',
        is_active: isActive !== undefined ? isActive : true,
      } as unknown as Record<string, unknown>
    );
  }

  /**
   * Remove user from company
   */
  static async removeFromCompany(userId: string, companyId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.USERS.ADD_TO_COMPANY(userId, companyId));
  }

  /**
   * Get user permissions
   */
  static async getPermissions(userId: string): Promise<UserPermission[]> {
    try {
      return this.get<UserPermission[]>(API_ENDPOINTS.USERS.PERMISSIONS(userId));
    } catch (error) {
      console.error('[UserService] Error fetching user permissions:', error);
      return [];
    }
  }

  /**
   * Grant permission to user
   */
  static async grantPermission(userId: string, permissionName: string, permissionType: string, grantedBy?: string): Promise<UserPermission> {
    return this.post<UserPermission>(
      API_ENDPOINTS.USERS.PERMISSIONS(userId),
      {
        permission_name: permissionName,
        permission_type: permissionType,
        granted_by: grantedBy,
      } as unknown as Record<string, unknown>
    );
  }

  /**
   * Revoke permission from user
   */
  static async revokePermission(userId: string, permissionId: string): Promise<void> {
    return this.delete(`${API_ENDPOINTS.USERS.PERMISSIONS(userId)}/${permissionId}`);
  }

  /**
   * Get user activity log
   */
  static async getActivity(userId: string, days?: number, limit?: number): Promise<UserActivity[]> {
    try {
      let endpoint = API_ENDPOINTS.USERS.ACTIVITY(userId);
      const params = new URLSearchParams();
      if (days) params.append('days', String(days));
      if (limit) params.append('limit', String(limit));
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      return this.get<UserActivity[]>(endpoint);
    } catch (error) {
      console.error('[UserService] Error fetching user activity:', error);
      return [];
    }
  }

  /**
   * Get user preferences
   */
  static async getPreferences(userId: string): Promise<UserPreferences> {
    try {
      return this.get<UserPreferences>(API_ENDPOINTS.USERS.PREFERENCES(userId));
    } catch (error) {
      console.error('[UserService] Error fetching user preferences:', error);
      return {};
    }
  }

  /**
   * Update user preferences
   */
  static async updatePreferences(userId: string, preferences: UserPreferences): Promise<UserPreferences> {
    return this.put<UserPreferences>(
      API_ENDPOINTS.USERS.PREFERENCES(userId),
      preferences as unknown as Record<string, unknown>
    );
  }
}
